import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { TS } from "../constants/testIds2";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { CaretLeft, CaretRight, PaperPlaneTilt, Plus, UploadSimple, DownloadSimple } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";

function mondayOf(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d.toISOString().slice(0, 10);
}
function addDays(dateStr, n) {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}
function fmtDay(dateStr) {
  return new Date(dateStr + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", day: "2-digit" });
}

const STATUS_BADGE = {
  draft: "bg-zinc-100 text-zinc-700 border-zinc-300",
  submitted: "bg-amber-50 text-amber-800 border-amber-200",
  approved: "bg-emerald-50 text-emerald-800 border-emerald-200",
  rejected: "bg-red-50 text-red-800 border-red-200",
};

export default function Timesheets() {
  const { user } = useAuth();
  const [week, setWeek] = useState(mondayOf(new Date().toISOString().slice(0, 10)));
  const [entries, setEntries] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [addProjectId, setAddProjectId] = useState("");
  const [loading, setLoading] = useState(false);
  const [manual, setManual] = useState({ project_name: "", client_name: "" });
  const fileRef = useRef(null);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(week, i)), [week]);

  const load = () => {
    setLoading(true);
    api.get("/timesheets/mine", { params: { week_start: week } })
      .then(({ data }) => setEntries(data.entries || []))
      .finally(() => setLoading(false));
    api.get("/timesheets/allocated-projects")
      .then(({ data }) => setAllocations((data || []).map((p) => ({ project_id: p.id, project_name: p.name, project_code: p.code }))))
      .catch(() => setAllocations([]));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [week]);

  // Group entries by project × date
  const grouped = useMemo(() => {
    const map = {};
    for (const e of entries) {
      const key = e.project_id || `manual:${e.project_name}`;
      if (!map[key]) map[key] = { key, project_id: e.project_id, project_name: e.project_name, client_name: e.client_name, project_code: e.project_id ? e.project_code : (e.client_name || "manual"), byDate: {}, status: e.status };
      map[key].byDate[e.date] = e;
    }
    // Add allocated projects that don't have entries yet
    for (const a of allocations) {
      if (!map[a.project_id]) map[a.project_id] = { key: a.project_id, project_id: a.project_id, project_name: a.project_name, project_code: a.project_code, byDate: {}, status: "draft" };
    }
    return Object.values(map);
  }, [entries, allocations]);

  const setHours = async (g, date, hoursValue) => {
    const hours = parseFloat(hoursValue) || 0;
    try {
      await api.post("/timesheets/entries", g.project_id
        ? { date, project_id: g.project_id, hours, task_notes: "", billable: true }
        : { date, project_name: g.project_name, client_name: g.client_name || null, hours, task_notes: "", billable: true });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addManual = () => {
    const name = manual.project_name.trim();
    if (!name) { toast.error("Type a project name"); return; }
    setHours({ project_name: name, client_name: manual.client_name.trim() }, days[0], 0);
    setManual({ project_name: "", client_name: "" });
  };

  const downloadTemplate = async () => {
    const { data } = await api.get("/timesheets/csv-template");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([data.content], { type: "text/csv" })); a.download = data.filename; a.click();
  };
  const importCsv = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/timesheets/import-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      data.errors?.length ? toast.warning(`Imported ${data.imported} of ${data.total_rows} rows · ${data.errors[0]}`) : toast.success(`Imported ${data.imported} entries`);
      if (data.imported) { const first = data.first_date; if (first) setWeek(mondayOf(first)); }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    if (fileRef.current) fileRef.current.value = "";
  };

  const submitWeek = async () => {
    try {
      const { data } = await api.post("/timesheets/submit-week", { week_start: week });
      toast.success(`Submitted ${data.updated} entries for approval`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addProject = () => {
    if (!addProjectId) return;
    const p = allocations.find((a) => a.project_id === addProjectId);
    if (!p) return;
    if (grouped.find((g) => g.project_id === p.project_id)) { toast.info("Project already on the sheet"); return; }
    setHours(p, days[0], 0);
    setAddProjectId("");
  };

  const weekTotal = useMemo(() => {
    let t = 0;
    for (const g of grouped) for (const d of days) t += g.byDate[d]?.hours || 0;
    return Math.round(t * 100) / 100;
  }, [grouped, days]);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-emerald-700">Time</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">My timesheet</h1>
          <p className="text-sm text-zinc-500 mt-2">Log hours against any project or task — allocated or typed manually — or upload a daily CSV. Submit weekly for approval.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setWeek(addDays(week, -7))} className="p-2 border border-zinc-200 rounded-sm hover:bg-zinc-50" data-testid="ts-prev-week"><CaretLeft size={16} /></button>
          <div className="font-mono-plex text-sm border border-zinc-200 rounded-sm px-3 py-1.5 bg-white">{week} → {addDays(week, 6)}</div>
          <button onClick={() => setWeek(addDays(week, 7))} className="p-2 border border-zinc-200 rounded-sm hover:bg-zinc-50" data-testid="ts-next-week"><CaretRight size={16} /></button>
          <Button onClick={submitWeek} className="btn-zenith rounded-sm ml-2" data-testid={TS.submitWeekBtn}><PaperPlaneTilt size={16} className="mr-1.5" /> Submit week</Button>
        </div>
      </div>

      {/* Add rows: manual / allocated / CSV */}
      <div className="border border-zinc-200 bg-white p-3 rounded-sm grid grid-cols-1 lg:grid-cols-3 gap-3" data-testid="ts-add-row-bar">
        <div className="flex gap-2 items-end">
          <div className="flex-1"><div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Add project / task manually</div>
            <Input value={manual.project_name} onChange={(e) => setManual({ ...manual, project_name: e.target.value })} placeholder="Project or task name" className="rounded-sm" data-testid="ts-manual-project" /></div>
          <Input value={manual.client_name} onChange={(e) => setManual({ ...manual, client_name: e.target.value })} placeholder="Client (optional)" className="rounded-sm w-36" data-testid="ts-manual-client" />
          <Button variant="outline" onClick={addManual} className="rounded-sm" data-testid="ts-manual-add"><Plus size={14} /></Button>
        </div>
        <div className="flex gap-2 items-end">
          <div className="flex-1"><div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">From my allocations</div>
            <Select value={addProjectId} onValueChange={setAddProjectId}>
              <SelectTrigger className="rounded-sm" data-testid="ts-alloc-select"><SelectValue placeholder={allocations.length ? "Pick allocated project" : "No allocations yet"} /></SelectTrigger>
              <SelectContent>{allocations.map((a) => <SelectItem key={a.project_id} value={a.project_id}>{a.project_name}</SelectItem>)}</SelectContent>
            </Select></div>
          <Button variant="outline" onClick={addProject} disabled={!addProjectId} className="rounded-sm" data-testid="ts-alloc-add"><Plus size={14} /></Button>
        </div>
        <div className="flex gap-2 items-end justify-end">
          <input ref={fileRef} type="file" accept=".csv,.xlsx" className="hidden" onChange={(e) => importCsv(e.target.files?.[0])} data-testid="ts-csv-input" />
          <Button variant="outline" onClick={downloadTemplate} className="rounded-sm" data-testid="ts-csv-template"><DownloadSimple size={14} className="mr-1" /> CSV template</Button>
          <Button variant="outline" onClick={() => fileRef.current?.click()} className="rounded-sm" data-testid="ts-csv-upload"><UploadSimple size={14} className="mr-1" /> Upload daily CSV</Button>
        </div>
      </div>

      {/* Week summary */}
      <div className="border border-zinc-200 bg-white p-3 rounded-sm flex items-center justify-between gap-2">
        <div className="text-xs text-zinc-500">CSV columns: sr_no, date, project, client, hours_worked, description. Rows without a date use today.</div>
        <div className="text-right">
          <div className="text-xs text-zinc-500">Week total</div>
          <div className="font-display text-2xl font-bold">{weekTotal}h</div>
        </div>
      </div>

      {/* Weekly grid */}
      <div className="overflow-x-auto border border-zinc-200 rounded-sm bg-white">
        <table className="hr-table" data-testid={TS.weekGrid}>
          <thead>
            <tr>
              <th className="min-w-[240px]">Project</th>
              {days.map((d) => (
                <th key={d} className="text-center min-w-[80px]">
                  <div className="text-[10px]">{fmtDay(d)}</div>
                  <div className="font-mono-plex text-[10px] text-zinc-500">{d.slice(5)}</div>
                </th>
              ))}
              <th className="text-center">Total</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={10} className="text-center py-6 text-zinc-500 text-sm">Loading…</td></tr>}
            {!loading && grouped.length === 0 && (
              <tr><td colSpan={10} className="text-center py-8 text-zinc-500 text-sm">
                Add a project or task above (or upload a CSV) to start logging hours.
              </td></tr>
            )}
            {grouped.map((g) => {
              const rowTotal = days.reduce((acc, d) => acc + (g.byDate[d]?.hours || 0), 0);
              const anyStatus = Object.values(g.byDate).map((e) => e.status).find((s) => s && s !== "draft") || "draft";
              return (
                <tr key={g.key}>
                  <td>
                    <div className="text-sm font-medium">{g.project_name}</div>
                    <div className="text-xs font-mono-plex text-zinc-500">{g.project_code}</div>
                  </td>
                  {days.map((d) => {
                    const entry = g.byDate[d];
                    const editable = !entry || entry.status === "draft" || entry.status === "rejected";
                    return (
                      <td key={d} className="text-center">
                        <input
                          type="number"
                          step="0.25"
                          min="0"
                          max="24"
                          disabled={!editable}
                          defaultValue={entry?.hours || ""}
                          onBlur={(e) => {
                            const v = parseFloat(e.target.value);
                            const cur = entry?.hours || 0;
                            if (!isNaN(v) && v !== cur) setHours(g, d, v);
                          }}
                          data-testid={TS.saveEntryBtn(g.project_code || g.project_name, d)}
                          className="w-16 text-center font-mono-plex text-sm border border-zinc-200 rounded-sm py-1 focus:outline-none focus:border-emerald-700 focus:ring-1 focus:ring-emerald-700 disabled:bg-zinc-50 disabled:text-zinc-400"
                        />
                      </td>
                    );
                  })}
                  <td className="text-center font-mono-plex text-sm font-semibold">{Math.round(rowTotal * 100) / 100}</td>
                  <td><Badge variant="outline" className={`rounded-sm ${STATUS_BADGE[anyStatus] || ""}`}>{anyStatus}</Badge></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="text-xs text-zinc-500">
        Tip: enter fractional hours (e.g. 4.5). Only <b>draft</b> or <b>rejected</b> cells are editable. Once submitted, only your manager can approve or reject.
      </div>
    </div>
  );
}
